import { createDateFormatter } from "@nexus/ui";
import { useContext, useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import { BarChart, CustomChart } from "echarts/charts";
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { InfoTip } from "@nexus/ui";
import {
  missingDataMode,
  useChartLifecycle,
  useChartAnimation,
  type MissingDataMode,
} from "@nexus/widget-kit";
import {
  defineLabels,
  firstError,
  MetricStat,
  resolveChartColors,
  resolveLabels,
  unwrap,
  UpdatingPill,
  Widget,
  WidgetContentReadyContext,
  WidgetFullscreenContext,
  WidgetIssueState,
  WidgetSkeleton,
  WidgetState,
} from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import {
  accountDays,
  buildWaterfallOption,
  summarizeAccounts,
  type WaterfallArrowStyle,
} from "./chart";
import { CDNSA_ACCOUNT_WATERFALL_CONTRACT, type CdnsaAccountWaterfallData } from "./types";

echarts.use([
  BarChart,
  CustomChart,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  TooltipComponent,
  CanvasRenderer,
]);

export interface CdnsaAccountWaterfallConfig {
  title?: string;
  /** `true` draws the chart in; off by default (drawn at once). */
  animateChart?: boolean;
  showLegend?: boolean;
  showStartingBalance?: boolean;
  summary?: "shown" | "hidden";
  arrowStyle?: WaterfallArrowStyle;
  labels?: Record<string, string>;
  /** Show gap (default) keeps every day of the period; Fit to data crops the days without data at its ends. */
  missingData?: MissingDataMode;
}
const LABELS = defineLabels([
  { key: "observed", name: "Observed accounts", default: "Observed" },
  { key: "lost", name: "Accounts no longer observed", default: "No longer observed" },
  { key: "newAccounts", name: "New accounts", default: "New accounts" },
]);
export const manifest: WidgetManifest = {
  id: "cdnsa-account-waterfall",
  name: "Daily distinct accounts",
  version: "1.0.0",
  description:
    "Daily distinct CDN/SA accounts observed, with newly observed and no-longer-observed accounts compared with the previous UTC day.",
  labels: LABELS,
  dataRequests: {
    series: {
      source: "cdnsa-account-waterfall",
      contract: CDNSA_ACCOUNT_WATERFALL_CONTRACT,
      configurable: true,
      sources: ["cdnsa-account-waterfall"],
    },
  },
};
export const defaultConfig: CdnsaAccountWaterfallConfig = {
  title: "Daily distinct accounts",
  showLegend: true,
  showStartingBalance: true,
  summary: "hidden",
  arrowStyle: "off",
};

export function Component({ data, config }: WidgetProps<CdnsaAccountWaterfallConfig>) {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const rendered = useRef(false);
  const { markContentReady } = useContext(WidgetContentReadyContext);
  const { fullscreen } = useContext(WidgetFullscreenContext);
  // Entry animation only when the author asks for it ("Animate chart").
  const motion = useChartAnimation(config);
  const series = unwrap<CdnsaAccountWaterfallData>(data.series);
  const error = firstError(data, ["series"]);
  const loading = data.series?.isLoading ?? false;
  const empty =
    !loading &&
    !error &&
    !series?.points.some((point) => point.accounts !== null || point.previous_accounts !== null);
  const labels = resolveLabels(LABELS, config);
  const missingData = missingDataMode(config.missingData);
  const draw = (host: HTMLElement, chart: echarts.ECharts) => {
    if (!series || error || empty) return;
    chart.setOption(
      buildWaterfallOption(
        accountDays(series.points, series.period, missingData),
        resolveChartColors(host),
        {
          labels,
          showLegend: config.showLegend !== false,
          showStartingBalance: config.showStartingBalance !== false,
          arrowStyle: config.arrowStyle ?? "off",
          motion,
        },
      ) as echarts.EChartsCoreOption,
      true,
    );
  };
  useEffect(() => {
    const host = hostRef.current;
    if (!host || loading || error || empty || !series) return;
    chartRef.current ??= echarts.init(host, null, { renderer: "canvas" });
    draw(host, chartRef.current);
    rendered.current = true;
    markContentReady();
  }, [
    series,
    loading,
    error,
    empty,
    config.showLegend,
    config.showStartingBalance,
    config.arrowStyle,
    config.labels,
    missingData,
    motion,
    markContentReady,
  ]);
  useChartLifecycle(hostRef, chartRef, draw);
  const ready = Boolean(series) && !error && !empty;
  const summary =
    ready && config.summary === "shown" && series ? summarizeAccounts(series.points) : null;
  const number = new Intl.NumberFormat("en", { maximumFractionDigits: 0 });
  const day = (value: string | undefined) =>
    value
      ? createDateFormatter("utc", { month: "short", day: "numeric" }, "en").format(
          new Date(`${value}T00:00:00Z`),
        )
      : undefined;
  return (
    <Widget
      title={config.title?.trim() || defaultConfig.title}
      intrinsicHeight={!fullscreen}
      actions={
        <>
          {loading && rendered.current ? <UpdatingPill /> : null}
          <InfoTip label="Daily distinct accounts">
            Counts cover complete UTC days and current filters. No longer observed does not mean
            blocked; new accounts may have returned.
          </InfoTip>
        </>
      }
    >
      {summary ? (
        <dl className="grid shrink-0 grid-cols-1 gap-x-6 gap-y-3 px-(--density-widget-body-padding) pt-2 pb-5 @sm/widget:grid-cols-3">
          <MetricStat
            size="standard"
            label="Latest day"
            value={
              summary.latest?.accounts == null || summary.latest.accounts === 0
                ? "—"
                : number.format(summary.latest.accounts)
            }
            detail={day(summary.latest?.day)}
          />
          <MetricStat
            size="standard"
            label="Daily average"
            value={summary.dailyAverage === null ? "—" : number.format(summary.dailyAverage)}
            detail="per measured UTC day"
          />
          <MetricStat
            size="standard"
            label="Peak day"
            value={summary.peak === null ? "—" : number.format(summary.peak.accounts)}
            detail={day(summary.peak?.day)}
          />
        </dl>
      ) : null}
      <div
        className={`relative min-h-(--widget-chart-min-height) w-full ${fullscreen ? "h-full min-h-0" : "flex-1"}`}
      >
        <div
          ref={hostRef}
          role="img"
          aria-label={`${labels.observed}, ${labels.lost} and ${labels.newAccounts} per UTC day. Accounts no longer observed are not confirmed blocked.`}
          className="absolute inset-0 h-full w-full"
          style={{ opacity: ready ? 1 : 0 }}
        />
        {error ? <WidgetIssueState error={error} className="absolute inset-0" /> : null}
        {empty ? (
          <WidgetState
            title="No complete account observations"
            description="No complete UTC days with valid account observations match the selected scope and filters."
            className="absolute inset-0"
          />
        ) : null}
        {loading && !rendered.current ? (
          <WidgetSkeleton shape="chart" className="absolute inset-0" />
        ) : null}
      </div>
    </Widget>
  );
}

const widgetModule: WidgetModule<CdnsaAccountWaterfallConfig> = {
  manifest,
  Component,
  defaultConfig,
  getDataRequirements: () => ({ series: { mode: "select", fields: ["points"] } }),
};
export default widgetModule;
